/**
 * ProviderCreateDialog Component
 *
 * Modal dialog for quick provider creation with auto-slug generation.
 */

import { Loader2, X } from "lucide-react";
import type { ReactElement, ChangeEvent, FormEvent } from "react";
import { useState, useCallback } from "react";

/** Form values for creating a provider */
export interface ProviderCreateFormValues {
  readonly name: string;
  readonly slug: string;
  readonly description: string;
  readonly logoUrl: string;
  readonly websiteUrl: string;
  readonly contactEmail: string;
}

/** Dialog props */
export interface ProviderCreateDialogProps {
  /** Whether the dialog is open */
  readonly isOpen: boolean;
  /** Whether the create operation is in progress */
  readonly isSubmitting: boolean;
  /** Error message to display */
  readonly error?: string;
  /** Called with form values when user submits */
  readonly onSubmit: (values: ProviderCreateFormValues) => void;
  /** Called when dialog is closed */
  readonly onClose: () => void;
}

/**
 * Auto-generate a slug from a provider name.
 */
function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Modal dialog for creating a new provider.
 */
export function ProviderCreateDialog({
  isOpen,
  isSubmitting,
  error,
  onSubmit,
  onClose,
}: ProviderCreateDialogProps): ReactElement | null {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [description, setDescription] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  const handleNameChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const newName = e.target.value;
      setName(newName);
      if (!slugManuallyEdited) {
        setSlug(generateSlug(newName));
      }
    },
    [slugManuallyEdited]
  );

  const handleSlugChange = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
    setSlug(e.target.value);
    setSlugManuallyEdited(true);
  }, []);

  const handleSubmit = useCallback(
    (e: FormEvent): void => {
      e.preventDefault();
      if (!name.trim() || !slug.trim()) return;
      // Slug format validation: lowercase alphanumeric with hyphens
      if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) return;
      onSubmit({ name, slug, description, logoUrl, websiteUrl, contactEmail });
    },
    [name, slug, description, logoUrl, websiteUrl, contactEmail, onSubmit]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg border border-border bg-background p-6 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold">Create Provider</h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1 rounded hover:bg-background-secondary transition-colors"
          >
            <X className="h-5 w-5 text-foreground-muted" />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 rounded-md border border-error/30 bg-error/10 p-3 text-sm text-error">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label htmlFor="provider-name" className="block text-sm font-medium mb-1">
              Name <span className="text-error">*</span>
            </label>
            <input
              id="provider-name"
              type="text"
              value={name}
              onChange={handleNameChange}
              placeholder="e.g. Acme Insurance"
              required
              disabled={isSubmitting}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            />
          </div>

          {/* Slug */}
          <div>
            <label htmlFor="provider-slug" className="block text-sm font-medium mb-1">
              Slug <span className="text-error">*</span>
            </label>
            <input
              id="provider-slug"
              type="text"
              value={slug}
              onChange={handleSlugChange}
              placeholder="e.g. acme-insurance"
              required
              disabled={isSubmitting}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            />
            <p
              className={`mt-1 text-xs ${slug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) ? "text-error" : "text-foreground-muted"}`}
            >
              {slug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)
                ? "Invalid slug: use lowercase alphanumeric with hyphens only (e.g. acme-insurance)"
                : "Lowercase alphanumeric with hyphens. Auto-generated from name."}
            </p>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="provider-description" className="block text-sm font-medium mb-1">
              Description
            </label>
            <textarea
              id="provider-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the provider"
              rows={2}
              disabled={isSubmitting}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            />
          </div>

          {/* Logo URL */}
          <div>
            <label htmlFor="provider-logo-url" className="block text-sm font-medium mb-1">
              Logo URL
            </label>
            <input
              id="provider-logo-url"
              type="url"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://example.com/logo.png"
              disabled={isSubmitting}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            />
          </div>

          {/* Website URL */}
          <div>
            <label htmlFor="provider-website-url" className="block text-sm font-medium mb-1">
              Website URL
            </label>
            <input
              id="provider-website-url"
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://example.com"
              disabled={isSubmitting}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            />
          </div>

          {/* Contact Email */}
          <div>
            <label htmlFor="provider-contact-email" className="block text-sm font-medium mb-1">
              Contact Email
            </label>
            <input
              id="provider-contact-email"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="contact@example.com"
              disabled={isSubmitting}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                isSubmitting ||
                !name.trim() ||
                !slug.trim() ||
                !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)
              }
              className="btn btn-primary"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Provider"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
